import { Form } from "@/components/ui";
import { MultiCategorySelectFormField } from "@/components/ui/FormFields/MultiCategorySelectFormField/MultiCategorySelectFormField";
import { useCreateMatterStatusPhase } from "@/lib/data/react-query";
import {
  buildMatterStatusGroups,
  type MatterStatusGroup,
} from "@/lib/data/static/matterStatics";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

export { buildMatterStatusGroups, type MatterStatusGroup };

type LeadStatusTransitionDropdownProps = {
  matterId: string;
  currentStatusId?: string;
  currentStatusLabel: string;
  matterStatusGroups: MatterStatusGroup[];
  canTransitionMatterStatus: boolean;
  isItemsLoading: boolean;
  isItemsError: boolean;
};

export function LeadStatusTransitionDropdown({
  matterId,
  currentStatusId,
  currentStatusLabel,
  matterStatusGroups,
  canTransitionMatterStatus,
  isItemsLoading,
  isItemsError,
}: LeadStatusTransitionDropdownProps) {
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const createMatterPhase = useCreateMatterStatusPhase();
  const form = useForm({
    defaultValues: {
      id: currentStatusId,
    },
    mode: "onSubmit",
  });
  const isTransitionBusy =
    createMatterPhase.isPending || !canTransitionMatterStatus;

  useEffect(() => {
    form.reset({
      id: currentStatusId,
    });
    setStatusDropdownOpen(false);
  }, [currentStatusId, form]);

  const handleStatusChange = async (value: string) => {
    if (!value || value === currentStatusId) {
      setStatusDropdownOpen(false);
      return;
    }

    try {
      setStatusDropdownOpen(false);
      await createMatterPhase.mutateAsync({
        dateTimeStart: new Date().toISOString(),
        matterId,
        statusId: value,
      });
    } catch {
      form.setValue("id", currentStatusId, {
        shouldDirty: false,
      });
    }
  };

  return (
    <div
      onClick={(event) => {
        event.stopPropagation();
      }}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
    >
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
          }}
        >
          <fieldset disabled={isTransitionBusy}>
            <MultiCategorySelectFormField
              placeholder={currentStatusLabel}
              searchPlaceholder="Search statuses..."
              emptyMessage="No status found."
              register={form.register("id")}
              items={matterStatusGroups}
              open={statusDropdownOpen}
              setOpen={setStatusDropdownOpen}
              isItemsError={isItemsError}
              isItemsLoading={isItemsLoading}
              onChange={handleStatusChange}
              buttonClassName="h-auto min-h-0 w-auto rounded-full border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 shadow-none hover:bg-blue-100 hover:text-blue-800"
            />
          </fieldset>
        </form>
      </Form>
    </div>
  );
}
